import { Group, MathUtils } from "three";
import { MULTIPLAYER_CONFIG } from "../../config/multiplayer.ts";
import {
  sameCharacterAppearance,
  type CharacterAppearance,
} from "../../sim/appearance.ts";
import type { MannequinMotionConfig, PlayerRenderSnapshot } from "../types.ts";
import { MannequinView } from "./mannequin.ts";

export interface RemotePlayerPresentation {
  readonly player: PlayerRenderSnapshot;
  readonly appearance: CharacterAppearance;
}

interface RemotePlayerView {
  readonly mannequin: MannequinView;
  appearance: CharacterAppearance;
  previous: PlayerRenderSnapshot;
  current: PlayerRenderSnapshot;
  transitionStartedAtMilliseconds: number;
  distanceTravelledMeters: number;
}

function copyPlayer(player: Readonly<PlayerRenderSnapshot>): PlayerRenderSnapshot {
  return { ...player, position: { ...player.position } };
}

function lerpAngle(from: number, to: number, alpha: number): number {
  const delta = Math.atan2(Math.sin(to - from), Math.cos(to - from));
  return from + delta * alpha;
}

export class RemotePlayerPopulationView {
  readonly object = new Group();
  private readonly views = new Map<string, RemotePlayerView>();
  private readonly motionConfig: Readonly<MannequinMotionConfig> | undefined;

  constructor(motionConfig?: Readonly<MannequinMotionConfig>) {
    this.motionConfig = motionConfig;
    this.object.name = "remote-player-population";
  }

  setPresentations(
    presentations: readonly Readonly<RemotePlayerPresentation>[],
  ): void {
    const retained = new Set<string>();
    for (const presentation of presentations) {
      const id = presentation.player.id;
      retained.add(id);
      const existing = this.views.get(id);
      if (existing === undefined) {
        const mannequin = new MannequinView(this.motionConfig, presentation.appearance);
        mannequin.object.name = `remote-player:${id}`;
        this.object.add(mannequin.object);
        const player = copyPlayer(presentation.player);
        this.views.set(id, {
          mannequin,
          appearance: presentation.appearance,
          previous: player,
          current: player,
          transitionStartedAtMilliseconds: performance.now(),
          distanceTravelledMeters: 0,
        });
      } else {
        if (!sameCharacterAppearance(existing.appearance, presentation.appearance)) {
          existing.mannequin.setAppearance(presentation.appearance);
          existing.appearance = presentation.appearance;
        }
        existing.distanceTravelledMeters += Math.hypot(
          presentation.player.position.x - existing.current.position.x,
          presentation.player.position.z - existing.current.position.z,
        );
        existing.previous = existing.current;
        existing.current = copyPlayer(presentation.player);
        existing.transitionStartedAtMilliseconds = performance.now();
      }
    }
    for (const [id, view] of this.views) {
      if (retained.has(id)) continue;
      view.mannequin.dispose();
      this.views.delete(id);
    }
  }

  update(_interpolationAlpha: number, animationTimeSeconds: number): void {
    const now = performance.now();
    for (const view of this.views.values()) {
      const alpha = MathUtils.clamp(
        (now - view.transitionStartedAtMilliseconds) /
          MULTIPLAYER_CONFIG.remoteInterpolationMilliseconds,
        0,
        1,
      );
      const previous = view.previous;
      const current = view.current;
      view.mannequin.setTransform(
        {
          x: MathUtils.lerp(previous.position.x, current.position.x, alpha),
          y: MathUtils.lerp(previous.position.y, current.position.y, alpha),
          z: MathUtils.lerp(previous.position.z, current.position.z, alpha),
        },
        lerpAngle(previous.yawRadians, current.yawRadians, alpha),
        {
          planarSpeedMetersPerSecond: MathUtils.lerp(
            previous.planarSpeedMetersPerSecond,
            current.planarSpeedMetersPerSecond,
            alpha,
          ),
          verticalSpeedMetersPerSecond: MathUtils.lerp(
            previous.verticalSpeedMetersPerSecond ?? 0,
            current.verticalSpeedMetersPerSecond ?? 0,
            alpha,
          ),
          grounded: current.grounded,
          isSprinting: current.isSprinting ?? false,
          animationTimeSeconds,
          distanceTravelledMeters: view.distanceTravelledMeters,
        },
        current.vehicleSeatRole ?? null,
        current.vehicleSteeringRadians ?? 0,
      );
    }
  }

  dispose(): void {
    for (const view of this.views.values()) view.mannequin.dispose();
    this.views.clear();
    this.object.removeFromParent();
  }
}
